import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  useTheme as useMuiTheme,
  Backdrop,
  Alert,
  alpha,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  CalendarToday as CalendarIcon,
  Info as InfoIcon,
  PowerSettingsNew as PowerIcon,
  DirectionsCar as VehicleIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import XLSX from 'sheetjs-style';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import axiosInstance from "../utlis/axiosInstance";
import moment from 'moment';
import { useTheme } from "../utlis/useTheme";
import DeviceSearchSelect, { DeviceOption } from "../components/DeviceSearchSelect";

interface Device {
  id: string;
  name: string;
}

interface ReportData {
  deviceId: string;
  deviceName: string;
  type: string;
  eventTime: string;
  ignitionStatus: string;
  latitude?: number;
  longitude?: number;
  speed?: number;
  address?: string;
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return format(date, 'dd:MM:yyyy');
};

const IgnitionReport: React.FC = () => {
  const { getPrimaryColor, getPrimaryLightColor, getPrimaryDarkColor } = useTheme();
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [reportData, setReportData] = useState<ReportData[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<DeviceOption[]>(
    id ? [{ id, name: id }] : []
  );
  const [fromDate, setFromDate] = useState<Date>(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0); // Today 12:00 AM
    return date;
  });
  const [toDate, setToDate] = useState<Date>(new Date());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [reportFetched, setReportFetched] = useState(false);

  const handleGenerateReport = async () => {
    if (!selectedDevices.length) {
      setError("Please select at least one device.");
      return;
    }

    const selectedDeviceIds = selectedDevices.map((device) => device.id);

    setLoading(true);
    setError(null);

    try {
      const fromDateStr = moment(fromDate)
        .subtract(5, "hours")
        .subtract(30, "minutes")
        .format("YYYY-MM-DDTHH:mm:ss[Z]");

      const toDateStr = moment(toDate)
        .subtract(5, "hours")
        .subtract(30, "minutes")
        .format("YYYY-MM-DDTHH:mm:ss[Z]");

      // Fetch ignition events for each selected device
      const ignitionEventTypes = ['ignition_on', 'ignition_off'];
      const allEventsPromises = selectedDeviceIds.flatMap(deviceId =>
        ignitionEventTypes.map(async (eventType) => {
          const queryParams = new URLSearchParams({
            from: fromDateStr,
            to: toDateStr,
            limit: '10000',
            device_id: deviceId,
            event_type: eventType
          });

          const response = await axiosInstance.get(`/gpstracker/eventhandler/?${queryParams.toString()}`);
          return response.data?.status ? (response.data?.responseData?.events || []) : [];
        })
      );

      const allEventsArrays = await Promise.all(allEventsPromises);
      const allEvents = allEventsArrays.flat();

      const transformedData = allEvents.map((event: any) => {
            const deviceId = event.device?.toString();
            const device = selectedDevices.find((d) => d.id === deviceId);

            return {
              deviceId: deviceId,
              deviceName: device?.name || `Device ${deviceId}`,
              type: event.event_type,
              eventTime: event.server_time,
              ignitionStatus: event.event_type === 'ignition_on' ? 'ON' : 'OFF',
              latitude: event.alarm?.latitude,
              longitude: event.alarm?.longitude,
              speed: event.alarm?.speed,
              address: event.alarm?.address
            };
          });

      setReportData(transformedData || []);
      setPage(0);
    } catch (error) {
      console.error("Error fetching report:", error);
      setError("Error generating report. Please try again.");
      setReportData([]);
    } finally {
      setLoading(false);
      setReportFetched(true);
    }
  };

  const handleExportExcel = async () => {
    if (reportData.length === 0) return;

    setDownloading(true);
    setDownloadProgress(0);

    try {
      // Create header rows like your CSV format
      const headerRows = [
        ["From DateTime", "", moment(fromDate).format('DD:MM:YYYY'), "", "", "", "", ""],
        ["To DateTime", "", moment(toDate).format('DD:MM:YYYY'), "", "", "", "", ""],
        ["Vehicle No", "", selectedDevices.map(device => device.name || device.id).join(', '), "", "", "", "", ""],
        ["", "", "", "", "", "", "", ""], // Empty row
        ["Sr.", "Vehicle Number", "Event Type", "Date & Time", "Status", "Latitude", "Longitude", "Speed", "Address"]
      ];

      // Create data rows
      const dataRows = reportData.map((row, index) => [
        index + 1,
        row.deviceName || "",
        row.type === 'ignition_on' ? 'Ignition ON' : 'Ignition OFF',
        formatDate(row.eventTime),
        row.ignitionStatus,
        row.latitude || "-",
        row.longitude || "-",
        row.speed || "-",
        row.address || "N/A"
      ]);

      // Combine header and data rows
      const allRows = [...headerRows, ...dataRows];

      setDownloadProgress(50);

      const worksheet = XLSX.utils.aoa_to_sheet(allRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Ignition Report");

      setDownloadProgress(75);

      // Generate Excel file
      XLSX.writeFile(workbook, `Ignition_Report_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`);

      setDownloadProgress(100);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setError('Failed to export report. Please try again.');
    } finally {
      setTimeout(() => {
        setDownloading(false);
        setDownloadProgress(0);
      }, 1000);
    }
  };

  const handleExportPDF = async () => {
    if (reportData.length === 0) return;

    setDownloading(true);
    setDownloadProgress(0);

    try {
      const doc = new jsPDF('landscape');

      doc.setFontSize(14);
      doc.text('Ignition Report', 14, 16);
      doc.setFontSize(10);
      doc.text(`From: ${moment(fromDate).format('DD:MM:YYYY')}`, 14, 24);
      doc.text(`To: ${moment(toDate).format('DD:MM:YYYY')}`, 110, 24);
      doc.text(`Devices: ${selectedDevices.map(device => device.name || device.id).join(', ')}`, 14, 30);

      const columns = [
        'Vehicle Number',
        'Event Type',
        'Date & Time',
        'Status',
        'Latitude',
        'Longitude',
        'Speed',
        'Address'
      ];

      const rows = reportData.map(row => [
        row.deviceName || '',
        row.type === 'ignition_on' ? 'Ignition ON' : 'Ignition OFF',
        formatDate(row.eventTime),
        row.ignitionStatus,
        row.latitude || '-',
        row.longitude || '-',
        row.speed || '-',
        row.address || 'N/A'
      ]);

      setDownloadProgress(30);

      // Use autotable plugin
      // @ts-ignore
      (autoTable as any)(doc, {
        head: [columns],
        body: rows,
        startY: 36,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [230, 236, 255] },
        theme: 'grid',
        didDrawPage: (dataArg: any) => {
          // page footer
          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.height ? pageSize.height : (pageSize as any).getHeight();
          doc.setFontSize(9);
          const pageNumber = doc.getNumberOfPages();
          doc.text(`Page ${pageNumber}`, doc.internal.pageSize.getWidth() - 30, pageHeight - 10);
        }
      });

      setDownloadProgress(80);

      doc.save(`Ignition_Report_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.pdf`);

      setDownloadProgress(100);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      setError('Failed to export PDF. Please try again.');
    } finally {
      setTimeout(() => {
        setDownloading(false);
        setDownloadProgress(0);
      }, 800);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = reportData.slice(startIndex, endIndex);
  const totalPages = Math.ceil(reportData.length / rowsPerPage);

  return (
    <Box
      sx={{
        fontFamily: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        backgroundColor: alpha(getPrimaryColor() || "#1D2B56", 0.02),
        minHeight: '100vh',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box
          sx={{
            padding: '1.5rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}
        >
          <Button
            onClick={() => navigate('/reports')}
            sx={{
              color: '#6b7280',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '2.5rem',
              height: '2.5rem',
              borderRadius: '0.5rem',
              minWidth: 'auto',
              '&:hover': {
                color: '#374151',
                backgroundColor: '#f3f4f6',
              },
            }}
          >
            <ArrowBackIcon sx={{ width: '1.5rem', height: '1.5rem' }} />
          </Button>
          <Typography
            variant="h5"
            sx={{
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#1f2937',
              margin: 0,
            }}
          >
            Ignition Report
          </Typography>
        </Box>
      </Box>

      <Box sx={{ padding: '1.5rem' }}>
        {/* Error Alert */}
        {error && (
          <Box sx={{ margin: '1rem 0' }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}

        {/* Filter Panel */}
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${alpha(getPrimaryColor() || "#1D2B56", 0.2)}`,
            padding: '1rem',
            marginBottom: '1.5rem',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'nowrap',
              justifyContent: 'flex-start',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: '160px',
                maxWidth: '200px',
                flex: '0 0 auto',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '0.25rem',
                }}
              >
                From Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  value={fromDate}
                  onChange={(newValue: Date | null) => newValue && setFromDate(newValue)}
                  maxDateTime={toDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: { fontSize: '0.75rem' }
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: '160px',
                maxWidth: '200px',
                flex: '0 0 auto',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '0.25rem',
                }}
              >
                To Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  value={toDate}
                  onChange={(newValue: Date | null) => newValue && setToDate(newValue)}
                  minDateTime={fromDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: { fontSize: '0.75rem' }
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: '160px',
                maxWidth: '200px',
                flex: '0 0 auto',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: '0.25rem',
                }}
              >
                Select Devices
              </Typography>
              <DeviceSearchSelect
                label="Add Device"
                multiple
                value={selectedDevices}
                onChange={(newValue) =>
                  setSelectedDevices((newValue as DeviceOption[]) || [])
                }
                maxSelection={10}
                onMaxSelectionReached={() =>
                  setError("Maximum 10 devices can be selected.")
                }
              />
              {/* Selected Devices Summary */}
              {selectedDevices.length > 0 && (
                <Box sx={{ marginTop: '0.5rem' }}>
                  {selectedDevices.length > 50 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: '#e3f2fd',
                        border: '1px solid #90caf9',
                        borderRadius: '8px',
                        padding: '0.5rem 0.75rem',
                        gap: '0.5rem',
                      }}
                    >
                      <Typography sx={{ color: '#0284F5', fontSize: '0.75rem', fontWeight: 500 }}>
                        {`${selectedDevices.length.toLocaleString()} Devices Selected`}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setSelectedDevices([])}
                        sx={{
                          minWidth: 'auto',
                          padding: '0.25rem 0.5rem',
                          color: '#0284F5',
                          fontSize: '0.7rem',
                          textTransform: 'none',
                          border: '1px solid #0284F5',
                          '&:hover': {
                            backgroundColor: 'rgba(2, 132, 245, 0.1)',
                          },
                        }}
                        variant="outlined"
                      >
                        Clear All
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {selectedDevices.map((device) => {
                        return (
                          <Box
                            key={device.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              backgroundColor: '#e3f2fd',
                              border: '1px solid #90caf9',
                              borderRadius: '16px',
                              padding: '0.25rem 0.5rem',
                              gap: '0.25rem',
                            }}
                          >
                            <Typography sx={{ color: '#0284F5', fontSize: '0.75rem' }}>
                              {device.name || device.id}
                            </Typography>
                            <Button
                              size="small"
                              onClick={() =>
                                setSelectedDevices((prev) =>
                                  prev.filter((selected) => selected.id !== device.id)
                                )
                              }
                              sx={{
                                minWidth: 'auto',
                                padding: '0',
                                color: '#0284F5',
                                '&:hover': {
                                  backgroundColor: 'rgba(2, 132, 245, 0.1)',
                                },
                              }}
                            >
                              ×
                            </Button>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                flexShrink: 0,
                marginLeft: 'auto',
              }}
            >
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleGenerateReport}
                  disabled={loading || !selectedDevices.length}
                sx={{
                  bgcolor: getPrimaryColor() || "#1D2B56",
                  color: 'white !important',
                  padding: '0.4rem 0.8rem !important',
                  borderRadius: '0.25rem !important',
                  fontWeight: '500 !important',
                  textTransform: 'none !important',
                  fontSize: '0.7rem !important',
                  transition: 'all 0.2s ease !important',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1) !important',
                  minWidth: '75px',
                  height: '32px',
                  '&:hover': {
                    bgcolor: getPrimaryLightColor(),
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15) !important',
                  },
                }}
              >
                {loading ? <CircularProgress size={16} color="inherit" /> : 'Generate'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportExcel}
                disabled={downloading || reportData.length === 0}
                sx={{
                  color: '#059669 !important',
                  borderColor: '#059669 !important',
                  padding: '0.4rem 0.8rem !important',
                  borderRadius: '0.25rem !important',
                  fontWeight: '500 !important',
                  textTransform: 'none !important',
                  fontSize: '0.7rem !important',
                  transition: 'all 0.2s ease !important',
                  minWidth: '65px',
                  height: '32px',
                  borderWidth: '1px !important',
                  '&:hover': {
                    backgroundColor: '#059669 !important',
                    color: 'white !important',
                  },
                }}
              >
                {downloading ? <CircularProgress size={16} color="inherit" /> : 'Excel'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<PdfIcon />}
                onClick={handleExportPDF}
                disabled={reportData.length === 0}
                sx={{
                  color: '#dc2626 !important',
                  borderColor: '#dc2626 !important',
                  padding: '0.4rem 0.8rem !important',
                  borderRadius: '0.25rem !important',
                  fontWeight: '500 !important',
                  textTransform: 'none !important',
                  fontSize: '0.7rem !important',
                  transition: 'all 0.2s ease !important',
                  minWidth: '65px',
                  height: '32px',
                  borderWidth: '1px !important',
                  '&:hover': {
                    backgroundColor: '#dc2626 !important',
                    color: 'white !important',
                  },
                }}
              >
                PDF
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Table */}
        {reportData.length > 0 ? (
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead
                  style={{
                    background: 'linear-gradient(to right, #eff6ff, #e0e7ff)',
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  <tr>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Vehicle Number</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Event Type</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date & Time</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Latitude</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Longitude</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Speed</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Address</th>
                  </tr>
                </thead>
                <tbody style={{ backgroundColor: 'white' }}>
                  {currentData.map((event, index) => (
                    <tr
                      key={index}
                      style={{
                        borderBottom: '1px solid #f3f4f6',
                        transition: 'all 0.15s ease',
                        backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#eff6ff')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#f9fafb')}
                    >
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {event.deviceName}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: 600,
                            gap: '4px',
                            backgroundColor: event.type === 'ignition_on' ? '#dcfce7' : '#fee2e2',
                            color: event.type === 'ignition_on' ? '#166534' : '#991b1b',
                            border: `1px solid ${event.type === 'ignition_on' ? '#bbf7d0' : '#fecaca'}`,
                          }}
                        >
                          <PowerIcon sx={{ fontSize: 14 }} />
                          {event.type === 'ignition_on' ? 'Ignition ON' : 'Ignition OFF'}
                        </Box>
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {formatDate(event.eventTime)}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937', fontWeight: 500 }}>
                        {event.ignitionStatus}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937', fontFamily: '"Monaco", "Menlo", "Ubuntu Mono", monospace' }}>
                        {event.latitude || "-"}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937', fontFamily: '"Monaco", "Menlo", "Ubuntu Mono", monospace' }}>
                        {event.longitude || "-"}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937' }}>
                        {event.speed || "-"}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap', fontSize: '0.875rem', color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>
                        {event.address || "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>

            {/* Pagination */}
            <Box
              sx={{
                backgroundColor: '#f9fafb',
                padding: '1rem 1.5rem',
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Typography sx={{ fontSize: '0.875rem', color: '#374151' }}>
                  Rows per page:
                </Typography>
                <select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  style={{
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    padding: '0.25rem 0.75rem',
                    fontSize: '0.875rem',
                    backgroundColor: 'white',
                    color: '#374151',
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <Typography sx={{ fontSize: '0.875rem', color: '#374151' }}>
                  {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, reportData.length)} of {reportData.length}
                </Typography>
                <Box sx={{ display: 'flex', gap: '0.25rem' }}>
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    style={{
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      backgroundColor: 'white',
                      color: '#374151',
                      cursor: page === 0 ? 'not-allowed' : 'pointer',
                      opacity: page === 0 ? 0.5 : 1,
                    }}
                  >
                    ←
                  </button>
                  <button
                    onClick={() => setPage(Math.min(Math.ceil(reportData.length / rowsPerPage) - 1, page + 1))}
                    disabled={page >= Math.ceil(reportData.length / rowsPerPage) - 1}
                    style={{
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      backgroundColor: 'white',
                      color: '#374151',
                      cursor: page >= Math.ceil(reportData.length / rowsPerPage) - 1 ? 'not-allowed' : 'pointer',
                      opacity: page >= Math.ceil(reportData.length / rowsPerPage) - 1 ? 0.5 : 1,
                    }}
                  >
                    →
                  </button>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          reportFetched && (
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: '0.75rem',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                padding: '3rem',
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <InfoIcon color="action" />
                <Typography sx={{ fontSize: '1rem', color: '#6b7280' }}>
                  No data found
                </Typography>
              </Box>
            </Box>
          )
        )}

        {/* Download Progress Dialog */}
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={downloading}
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              p: 4,
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            <CircularProgress
              variant="determinate"
              value={downloadProgress}
              size={60}
              thickness={4}
              sx={{ mb: 2 }}
            />
            <Typography variant="h6" color="text.primary">
              {downloadProgress < 100 ? 'Preparing Download...' : 'Download Complete!'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {downloadProgress}% Complete
            </Typography>
          </Box>
        </Backdrop>
      </Box>
    </Box>
  );
};

export default IgnitionReport; 